import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Box, Text } from 'grommet';
import { Spinning } from '@Components/Control/Loaders';

// ─── Constants ────────────────────────────────────────────────────────────────

export const TABS = ['Overview', 'Weekly Trends', 'Performance', 'Estimated KPIs', 'YT Audits'];

const currentYear = new Date().getFullYear();
export const YEAR_OPTIONS = Array.from({ length: currentYear - 2023 }, (_, i) => {
  const year = String(2024 + i);
  return { api: year, display: year };
});

export const MONTH_OPTIONS = [
  { api: '01', display: 'January' },
  { api: '02', display: 'February' },
  { api: '03', display: 'March' },
  { api: '04', display: 'April' },
  { api: '05', display: 'May' },
  { api: '06', display: 'June' },
  { api: '07', display: 'July' },
  { api: '08', display: 'August' },
  { api: '09', display: 'September' },
  { api: '10', display: 'October' },
  { api: '11', display: 'November' },
  { api: '12', display: 'December' },
];

export const CHART_TOOLTIP = {
  contentStyle: {
    background: '#0E2435',
    border: 'none',
    borderRadius: '10px',
    color: '#E9F4FF',
    fontSize: '12px',
  },
  labelStyle: { color: '#E9F4FF', fontWeight: 600 },
  itemStyle: { color: '#FFFFFF' },
  wrapperStyle: { color: '#FFFFFF' },
};

export const AXIS_TICK = { fill: '#94A3B8', fontSize: 13 };
export const GRID_STYLE = { strokeDasharray: '3 3', stroke: '#E6EEF6' };

export const REVENUE_COLORS = {
  totalPartnerRevenue: '#0EA5A2',
  redflagShare: '#2563EB',
  payableRevenue: '#F97316',
};

export const EST_REVENUE_COLORS = {
  totalEstimatedRevenue: '#A855F7',
  estimatedRedflagShare: '#F97316',
  estimatedPayableRevenue: '#6366F1',
};

export const WEEKLY_COLORS = {
  newCreators: '#10B981',
  newAssets: '#6366F1',
  engagedViews: '#F59E0B',
};

export const PERF_COLORS = {
  low: '#EF4444',
  high: '#10B981',
  rpm: '#A855F7',
  zeroView: '#F97316',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function formatMoney(value) {
  if (value === null || value === undefined) return '—';
  return `$${Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function formatMonthLabel(yyyyMM) {
  if (!yyyyMM) return '—';
  const [year, month] = yyyyMM.split('-');
  if (!month) return yyyyMM;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleString('en-US', {
    month: 'short',
    year: 'numeric',
  });
}

export function formatMonthShort(yyyyMM) {
  if (!yyyyMM) return '';
  const [year, month] = yyyyMM.split('-');
  if (!month) return yyyyMM;
  const d = new Date(Number(year), Number(month) - 1, 1);
  return `${d.toLocaleString('en-US', { month: 'short' })} '${String(year).slice(2)}`;
}

export function formatK(value) {
  if (!value && value !== 0) return '';
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}k`;
  return `$${Number(value).toFixed(0)}`;
}

export function formatViews(value) {
  if (!value && value !== 0) return '';
  if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `${(value / 1000).toFixed(1)}k`;
  return String(Math.round(value));
}

export function ytVideoUrl(videoId) {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const StyledTabButton = styled.button`
  padding: 0.6rem 1.25rem;
  border-radius: var(--app-radius);
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.6;
  }
`;

export function TabBar({ activeTab, onTabChange, page }) {
  return (
    <Box
      direction="row"
      gap="0.5rem"
      flex={false}
      style={{
        border: '1px solid var(--page-border)',
        borderRadius: 'var(--app-radius)',
        padding: '0.25rem',
      }}
    >
      {TABS.map((tab) => {
        const isActive = tab === activeTab;
        return (
          <StyledTabButton
            key={tab}
            onClick={() => onTabChange(tab)}
            style={{
              background: isActive ? 'var(--page-highlight)' : 'transparent',
              border: isActive ? '1px solid var(--page-link)' : `1px solid ${page.secondaryText}`,
              borderBottom: isActive ? '2px solid var(--page-link)' : `1px solid ${page.secondaryText}`,
            }}
          >
            <Text
              size="0.9rem"
              weight={isActive ? 700 : 500}
              color={isActive ? '#FFFFFF' : page.secondaryText}
            >
              {tab}
            </Text>
          </StyledTabButton>
        );
      })}
    </Box>
  );
}

TabBar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  page: PropTypes.object.isRequired,
};

export function PanelTitle({ label, action }) {
  return (
    <Box direction="row" justify="between" align="center" flex={false}>
      <Text size="1rem" weight={700} color="var(--page-primary-text)">
        {label}
      </Text>
      {action}
    </Box>
  );
}

PanelTitle.propTypes = {
  label: PropTypes.string.isRequired,
  action: PropTypes.node,
};

export function EmptyChart({ loading = false, text = 'No data available.' }) {
  return (
    <Box height="19rem" align="center" justify="center">
      {loading
        ? <Spinning size="large" />
        : <Text size="0.85rem" color="var(--page-hint-text)">{text}</Text>}
    </Box>
  );
}

EmptyChart.propTypes = { loading: PropTypes.bool, text: PropTypes.string };

export function ColorDot({ color }) {
  return (
    <Box
      width="8px"
      height="8px"
      round="full"
      flex={false}
      style={{ background: color }}
    />
  );
}

ColorDot.propTypes = { color: PropTypes.string.isRequired };

export function EmptyTable({ page, loading = false }) {
  return (
    <Box pad={{ vertical: '2rem' }} align="center">
      {loading
        ? <Spinning size="medium" />
        : <Text size="0.85rem" color={page.secondaryText}>No data available.</Text>}
    </Box>
  );
}

EmptyTable.propTypes = { page: PropTypes.object.isRequired, loading: PropTypes.bool };
